import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const EmployerPerformanceTab = ({ dateRange }) => {
  const [sortBy, setSortBy] = useState('satisfaction');
  const [filterIndustry, setFilterIndustry] = useState('all');

  const sortOptions = [
    { value: 'satisfaction', label: 'Satisfaction Score' },
    { value: 'hiring-velocity', label: 'Hiring Velocity' },
    { value: 'retention', label: 'Retention Rate' },
    { value: 'total-hires', label: 'Total Hires' }
  ];

  const industryFilterOptions = [
    { value: 'all', label: 'All Industries' },
    { value: 'it', label: 'Information Technology' },
    { value: 'finance', label: 'Banking & Finance' },
    { value: 'healthcare', label: 'Healthcare' }
  ];

  const employerRankings = [
    {
      rank: 1,
      company: 'Tech Innovations Pvt Ltd',
      industry: 'IT',
      totalHires: 145,
      satisfactionScore: 4.8,
      retentionRate: 94,
      hiringVelocity: 12,
      avgTimeToHire: 18,
      offerAcceptanceRate: 92
    },
    {
      rank: 2,
      company: 'DataTech Solutions',
      industry: 'IT',
      totalHires: 128,
      satisfactionScore: 4.7,
      retentionRate: 91,
      hiringVelocity: 10,
      avgTimeToHire: 21,
      offerAcceptanceRate: 89
    },
    {
      rank: 3,
      company: 'Global Consulting Group',
      industry: 'Consulting',
      totalHires: 98,
      satisfactionScore: 4.6,
      retentionRate: 88,
      hiringVelocity: 8,
      avgTimeToHire: 25,
      offerAcceptanceRate: 85
    },
    {
      rank: 4,
      company: 'FinanceHub India',
      industry: 'Finance',
      totalHires: 85,
      satisfactionScore: 4.5,
      retentionRate: 86,
      hiringVelocity: 7,
      avgTimeToHire: 28,
      offerAcceptanceRate: 83
    },
    {
      rank: 5,
      company: 'HealthCare Plus',
      industry: 'Healthcare',
      totalHires: 72,
      satisfactionScore: 4.4,
      retentionRate: 84,
      hiringVelocity: 6,
      avgTimeToHire: 30,
      offerAcceptanceRate: 80
    }
  ];

  const hiringVelocityData = [
    { month: 'Aug', velocity: 8 },
    { month: 'Sep', velocity: 10 },
    { month: 'Oct', velocity: 12 },
    { month: 'Nov', velocity: 14 },
    { month: 'Dec', velocity: 11 },
    { month: 'Jan', velocity: 15 }
  ];

  const performanceMetrics = [
    { metric: 'Hiring Speed', value: 85 },
    { metric: 'Candidate Quality', value: 92 },
    { metric: 'Offer Acceptance', value: 88 },
    { metric: 'Retention', value: 90 },
    { metric: 'Satisfaction', value: 94 },
    { metric: 'Communication', value: 87 }
  ];

  const satisfactionBreakdown = [
    { category: 'Interview Process', score: 4.7, feedback: 'Well-structured and professional' },
    { category: 'Onboarding Experience', score: 4.6, feedback: 'Comprehensive and supportive' },
    { category: 'Work Environment', score: 4.8, feedback: 'Collaborative and innovative' },
    { category: 'Growth Opportunities', score: 4.5, feedback: 'Clear career progression' },
    { category: 'Compensation', score: 4.4, feedback: 'Competitive packages' }
  ];

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    if (rank === 2) return 'bg-gray-400/10 text-gray-600 border-gray-400/20';
    if (rank === 3) return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    return 'bg-muted text-muted-foreground border-border';
  };

  const handleExportPerformance = () => {
    const csvContent = [
      ['Rank', 'Company', 'Industry', 'Total Hires', 'Satisfaction', 'Retention', 'Hiring Velocity'],
      ...employerRankings?.map(row => [
        row?.rank,
        row?.company,
        row?.industry,
        row?.totalHires,
        row?.satisfactionScore,
        `${row?.retentionRate}%`,
        row?.hiringVelocity
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `employer-performance-${Date.now()}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          className="w-full md:w-48"
        />
        <Select
          options={industryFilterOptions}
          value={filterIndustry}
          onChange={setFilterIndustry}
          className="w-full md:w-48"
        />
        <Button variant="outline" iconName="Download" onClick={handleExportPerformance}>
          Export Performance
        </Button>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Top Employer Rankings</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rank</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Industry</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Hires</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Satisfaction</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Retention</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Hiring Velocity</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employerRankings?.map((employer) => (
                <tr key={employer?.rank} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full border font-semibold text-sm ${getRankBadgeColor(employer?.rank)}`}>
                      {employer?.rank}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Building2" size={16} color="var(--color-primary)" />
                      </div>
                      <span className="text-sm font-medium text-card-foreground">{employer?.company}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{employer?.industry}</td>
                  <td className="py-3 px-4 text-sm font-medium text-card-foreground">{employer?.totalHires}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={14} color="var(--color-warning)" />
                      <span className="text-sm font-semibold text-card-foreground">{employer?.satisfactionScore}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-success">{employer?.retentionRate}%</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-card-foreground">{employer?.hiringVelocity} hires/month</td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Hiring Velocity Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hiringVelocityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="velocity" fill="var(--color-accent)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Performance Radar</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={performanceMetrics}>
              <PolarGrid stroke="var(--color-border)" />
              <PolarAngleAxis dataKey="metric" stroke="var(--color-muted-foreground)" />
              <PolarRadiusAxis stroke="var(--color-muted-foreground)" />
              <Radar name="Performance" dataKey="value" stroke="var(--color-primary)" fill="var(--color-primary)" fillOpacity={0.3} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Candidate Satisfaction Breakdown</h3>
        <div className="space-y-4">
          {satisfactionBreakdown?.map((item, idx) => (
            <div key={idx} className="p-4 rounded-lg border border-border hover:border-primary transition-smooth">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-card-foreground">{item?.category}</h4>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} color="var(--color-warning)" />
                  <span className="text-sm font-semibold text-card-foreground">{item?.score}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{item?.feedback}</p>
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-warning transition-smooth"
                  style={{ width: `${(item?.score / 5) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg. Retention Rate</p>
              <h3 className="text-2xl font-bold text-card-foreground">88.6%</h3>
            </div>
          </div>
          <p className="text-xs text-success">+4.2% from last quarter</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg. Time to Hire</p>
              <h3 className="text-2xl font-bold text-card-foreground">24 days</h3>
            </div>
          </div>
          <p className="text-xs text-success">-3 days improvement</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Offer Acceptance</p>
              <h3 className="text-2xl font-bold text-card-foreground">86.8%</h3>
            </div>
          </div>
          <p className="text-xs text-success">+2.1% increase</p>
        </div>
      </div>
    </div>
  );
};

export default EmployerPerformanceTab;